import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  ArrowLeft, 
  Palette, 
  CheckCircle, 
  AlertTriangle,
  Eye,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminBrandingSettings({ user }) {
  const navigate = useNavigate();
  const [currentBranding, setCurrentBranding] = useState('scc');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrandingSettings();
  }, []);

  const fetchBrandingSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/branding-settings`, { withCredentials: true });
      setCurrentBranding(response.data.branding_mode || 'scc');
    } catch (error) {
      // Default to SCC if not set
      setCurrentBranding('scc');
    } finally {
      setLoading(false);
    }
  };

  const handleBrandingChange = async (mode) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/branding-settings`, 
        { branding_mode: mode },
        { withCredentials: true }
      );
      setCurrentBranding(mode);
      
      // Also save to localStorage for immediate effect
      localStorage.setItem('brandingMode', mode);
      
      toast.success(`Branding switched to ${mode === 'scc' ? 'SCC' : 'Generic'} mode`);
      
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('brandingChange', { detail: { mode } }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update branding");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin")}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Palette className="w-6 h-6 text-yellow-400" />
              Branding Settings
            </h1>
            <p className="text-slate-400 text-sm">Toggle between SCC branding and generic branding</p>
          </div>
        </div>

        {/* Current Status */}
        <Card className="p-6 bg-slate-800/50 border-slate-700 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-3 h-3 rounded-full ${currentBranding === 'scc' ? 'bg-yellow-400' : 'bg-blue-400'}`}></div>
            <h2 className="text-lg font-semibold text-white">
              Current: {currentBranding === 'scc' ? 'SCC Branding' : 'Generic Branding'}
            </h2>
          </div>
          <p className="text-slate-400 text-sm">
            {currentBranding === 'scc' 
              ? 'The portal displays Singapore Cricket Club branding including logo, colors, and official text.'
              : 'The portal displays generic football club branding without SCC-specific elements.'}
          </p>
        </Card>

        {/* Branding Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* SCC Branding */}
          <div 
            data-testid="scc-branding-card"
            onClick={() => handleBrandingChange('scc')}
            className={`p-6 cursor-pointer transition-all rounded-xl border ${
              currentBranding === 'scc' 
                ? 'bg-yellow-500/10 border-2 border-yellow-500' 
                : 'bg-slate-800/50 border-slate-700 hover:border-yellow-500/50'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center border-2 border-yellow-500/50">
                  <img 
                    src="https://sccfootball.com/wp-content/uploads/2024/07/yellow-shadow.png"
                    alt="SCC Logo"
                    className="w-8 h-8"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div>
                  <h3 className="font-bold text-white">SCC Branding</h3>
                  <p className="text-xs text-yellow-400">Singapore Cricket Club</p>
                </div>
              </div>
              {currentBranding === 'scc' && (
                <CheckCircle className="w-6 h-6 text-yellow-400" />
              )}
            </div>
            
            <div className="space-y-2 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                <span>SCC Logo displayed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                <span>Gold/Yellow accent colors</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                <span>Official SCC text & disclaimers</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                <span>Est. 1852 tagline</span>
              </div>
            </div>

            {saving && currentBranding !== 'scc' && (
              <div className="mt-4 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-yellow-400 animate-spin" />
              </div>
            )}
          </div>

          {/* Generic Branding */}
          <div 
            data-testid="generic-branding-card"
            onClick={() => handleBrandingChange('generic')}
            className={`p-6 cursor-pointer transition-all rounded-xl border ${
              currentBranding === 'generic' 
                ? 'bg-blue-500/10 border-2 border-blue-500' 
                : 'bg-slate-800/50 border-slate-700 hover:border-blue-500/50'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center border-2 border-blue-500/50">
                  <span className="text-2xl">⚽</span>
                </div>
                <div>
                  <h3 className="font-bold text-white">Generic Branding</h3>
                  <p className="text-xs text-blue-400">Football Club Portal</p>
                </div>
              </div>
              {currentBranding === 'generic' && (
                <CheckCircle className="w-6 h-6 text-blue-400" />
              )}
            </div>
            
            <div className="space-y-2 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>Generic football icon</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>Blue accent colors</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>Generic club text</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>No SCC references</span>
              </div>
            </div>

            {saving && currentBranding !== 'generic' && (
              <div className="mt-4 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />
              </div>
            )}
          </div>
        </div>

        {/* Warning */}
        <Card className="mt-6 p-4 bg-amber-500/10 border-amber-500/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-amber-400 font-medium mb-1">Important Note</p>
              <p className="text-slate-300">
                Changing branding affects the public-facing pages including the landing page, 
                member cards, and registration forms. Users may need to refresh their browser 
                to see the changes.
              </p>
            </div>
          </div>
        </Card>

        {/* Preview Button */}
        <div className="mt-6 flex justify-center">
          <Button
            variant="outline"
            onClick={() => window.open('/?preview=true', '_blank')}
            className="border-slate-600 text-slate-300"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview Landing Page
          </Button>
          <p className="text-xs text-slate-500 mt-2 text-center ml-4">
            Tip: Use incognito mode or log out to see the landing page
          </p>
        </div>
      </div>
    </div>
  );
}
